<template>
  <div
    class="notion-workspace"
    :class="[drawerOpen ? 'drawer-open-workspace' : '', { 'section-panel-layout': sectionPanelOpen }]"
    :style="workspaceStyle"
    ref="workspaceRef"
    @scroll="updateActiveOutline"
    @mouseup="captureSelection"
  >
    <header class="workspace-topbar">
      <div class="workspace-topbar__identity">
        <button type="button" class="ui-button ui-button--quiet" @click="$emit('back')">Back</button>
        <div class="workspace-document-title">
          <span>Focused writing workspace</span>
          <strong>{{ store.researchTopic || 'Mini-Research Proposal' }}</strong>
        </div>
      </div>

      <div
        v-if="store.stage === 'drafting'"
        class="draft-progress"
        role="progressbar"
        aria-label="Draft progress"
        aria-valuemin="0"
        aria-valuemax="100"
        :aria-valuenow="draftProgress"
      >
        <div class="draft-progress__summary">
          <span>Draft progress</span>
          <strong>{{ draftProgress }}%</strong>
        </div>
        <div class="draft-progress__track" aria-hidden="true">
          <span :style="{ width: `${draftProgress}%` }"></span>
        </div>
        <small>{{ completedSections }} of {{ draftSections.length }} sections started</small>
      </div>

    </header>

    <div class="workspace-body">
      <aside class="page-outline document-outline" aria-label="Document outline">
        <div class="document-outline__heading">
          <span>Document</span>
          <strong>Outline</strong>
        </div>
        <nav>
          <button
            v-for="item in outlineItems"
            :key="item.id"
            type="button"
            :class="activeOutlineId === item.id ? 'outline-active' : ''"
            :aria-current="activeOutlineId === item.id ? 'location' : undefined"
            @click="scrollTo(item.id)"
          >
            <span class="outline-marker" aria-hidden="true"></span>
            <span>{{ item.label }}</span>
          </button>
        </nav>
        <p>Use the outline to move through your proposal.</p>
      </aside>

      <main class="notion-page">
        <div v-if="showAiUnavailable" class="ai-unavailable-notice ui-notice" role="status" aria-live="polite">
          <strong>AI features are unavailable.</strong>
          <span>You can keep writing, save your draft, and submit it. Ask an administrator to configure the OpenAI API key.</span>
        </div>

        <div class="writing-paper">
          <div class="paper-masthead" aria-hidden="true">
            <span>Mini-research proposal</span>
            <span>Working document</span>
          </div>

          <section id="prompt" class="notion-section prompt-section">
            <div class="section-kicker">Essay Prompt</div>
            <div class="prompt-box">{{ store.assignmentPrompt }}</div>
          </section>

          <section v-if="isReadonly && store.scoreResult" id="submitted-score" class="notion-section submitted-score-section">
            <div class="section-kicker">AI Score and Feedback</div>
            <p class="score-disclaimer">AI-generated scores and feedback are for reference only. The score given by the module teacher is final.</p>
            <div class="submitted-score-card">
              <div class="submitted-score-main">{{ store.scoreResult.score ?? '-' }} / 4</div>
              <div class="submitted-score-details">
                <article v-for="dimension in store.scoreResult.dimensions || []" :key="dimension.name" class="submitted-score-dimension">
                  <strong>{{ dimension.name }}</strong>
                  <span>{{ dimension.comment }}</span>
                </article>
                <p v-if="store.scoreResult.summary" class="submitted-score-summary">{{ store.scoreResult.summary }}</p>
              </div>
            </div>
          </section>

          <section v-if="isReadonly && hasTeacherReview" id="teacher-review" class="notion-section submitted-score-section">
            <div class="section-kicker">Teacher Score and Feedback</div>
            <div class="submitted-score-card teacher-review-card">
              <div class="submitted-score-main">{{ store.teacherScore ?? '-' }} / 4</div>
              <div class="submitted-score-details">
                <p class="submitted-score-summary">{{ store.teacherFeedback || 'No teacher feedback yet.' }}</p>
              </div>
            </div>
          </section>

          <section id="topic-selection" class="notion-section topic-card">
            <div>
              <div class="section-kicker">Topic Selection</div>
              <h2 v-if="store.stage !== 'drafting'">Choose a focused research topic</h2>
              <div v-else class="topic-title-row">
                <div class="confirmed-topic">{{ store.researchTopic }}</div>
                <button v-if="!isReadonly" class="btn-secondary compact-btn topic-edit-btn" @click="beginTopicEdit">Edit topic</button>
              </div>
            </div>
            <div class="topic-meta-row">
              <div class="cefr-control" aria-label="CEFR level">
                <span>CEFR</span>
                <button
                  v-for="level in cefrLevels"
                  :key="level"
                  :class="store.cefrLevel === level ? 'cefr-active' : ''"
                  :disabled="isReadonly"
                  @click="store.setCefrLevel(level)"
                >
                  {{ level }}
                </button>
              </div>
            </div>
            <div v-if="store.stage !== 'drafting'" class="topic-entry">
              <input v-model="topicInput" maxlength="500" placeholder="Type your research topic..." :disabled="isReadonly || draftStarting" @input="clearTopicImprovement" @keydown.enter.prevent="improveTopicIdea" />
              <button class="btn-secondary" @click="improveTopicIdea" :disabled="isReadonly || draftStarting || topicImproving || !topicInput.trim()">
                {{ topicImproving ? 'Improving...' : 'AI improve' }}
              </button>
              <button class="btn-primary" @click="notionStartDrafting" :disabled="isReadonly || draftStarting || topicImproving || !topicInput.trim()">
                {{ draftStarting ? 'Starting...' : 'Start draft' }}
              </button>
            </div>
            <div v-if="store.stage !== 'drafting' && topicFeedback" class="topic-feedback">{{ topicFeedback }}</div>
            <details v-if="store.stage !== 'drafting'" class="topic-options" :open="topicOptionsOpen">
              <summary>Suggested topics · {{ topicSourceLabel }}</summary>
              <div class="topic-grid">
                <button
                  v-for="topic in displayedTopicSuggestions"
                  :key="topic"
                  class="topic-chip"
                  :disabled="isReadonly || draftStarting"
                  @click="chooseTopic(topic)"
                >
                  {{ topic }}
                </button>
              </div>
            </details>
            <div v-if="store.stage !== 'drafting' && improvedTopicSuggestions.length" class="topic-options">
              <p class="field-label">{{ topicAiSource === 'preview' ? 'Preview suggestions (simulated)' : 'AI suggestions' }} · choose one, then click Start draft</p>
              <div class="topic-grid">
                <button v-for="topic in improvedTopicSuggestions" :key="topic" class="topic-chip" :disabled="isReadonly || draftStarting" @click="chooseTopic(topic)">{{ topic }}</button>
              </div>
            </div>
          </section>

          <section v-if="store.stage === 'drafting'" id="draft" class="notion-section draft-page">
            <article
              v-for="section in draftSections"
              :id="section.key"
              :key="section.key"
              class="draft-section"
            >
              <div class="draft-section-heading">
                <h3>{{ section.label }}</h3>
                <button
                  v-if="section.key === 'references'"
                  class="btn-secondary compact-btn"
                  @click="generateReferencesSection"
                  :disabled="isReadonly || !store.references.length"
                >
                  Generate from library
                </button>
              </div>

              <details v-if="experiment" class="hint-details" open><summary>Rubric focus</summary><ul><li v-for="criterion in experiment.rubric?.[section.key] || []" :key="criterion.id">{{ criterion.description }}</li></ul></details>
              <details v-else-if="section.key !== 'references'" class="hint-details" open>
                <summary>Hint</summary>
                <div class="hint-panel">
                  <div v-if="sectionMeta(section).framework.length" class="framework-list">
                    <div class="framework-title">Framework</div>
                    <div v-for="item in sectionMeta(section).framework" :key="item" class="framework-row">{{ item }}</div>
                  </div>
                  <div v-for="(question, index) in sectionMeta(section).hintQuestions" :key="question" class="hint-row">
                    <span class="slot-tag">Q{{ index + 1 }}</span>
                    <span>{{ question }}</span>
                  </div>
                </div>
              </details>

              <div
                v-if="section.key === 'references' && !experiment"
                class="notion-editor references-editor"
                :contenteditable="!isReadonly"
                role="textbox"
                aria-label="References draft"
                aria-multiline="true"
                data-placeholder="Generate from reference library or write APA references here..."
                v-html="store.draftSections.references"
                @input="updateReferencesDraft"
                @mouseup="captureSelection"
                @keyup="captureSelection"
              ></div>
              <textarea
                v-else
                class="notion-editor"
                v-model="store.draftSections[section.key]"
                :readonly="isReadonly"
                :data-section="section.key"
                :placeholder="`Write your ${section.label.toLowerCase()} section...`"
                @input="resizeDraftEditor"
                @select="captureSelection"
                @keyup="captureSelection"
              />
              <div v-if="experiment" class="section-improve-action"><button class="ui-button ui-button--primary" :aria-label="`AI improve ${section.label}`" :disabled="isReadonly || experimentBusy || (!store.draftSections[section.key]?.trim() && !(section.key === 'references' && store.references.length))" @click="drawerOpen = false; $emit('section-improve', section.key)">AI improve</button></div>
            </article>
          </section>

          <div v-if="store.stage === 'drafting'" class="submit-footer workspace-actions">
            <template v-if="!isReadonly">
              <span v-if="saveStatus" class="save-status" role="status">{{ saveStatus }}</span>
              <button type="button" class="ui-button ui-button--quiet" :disabled="saveLoading" @click="$emit('save')">
                {{ saveLoading ? 'Saving...' : 'Save draft' }}
              </button>
              <button v-if="!experiment" type="button" class="ui-button ui-button--quiet" :disabled="!store.combinedDraft.trim() || scoreLoading" @click="$emit('score')">
                {{ scoreLoading ? 'Scoring...' : 'Score' }}
              </button>
              <button type="button" class="ui-button ui-button--primary" :disabled="!store.combinedDraft.trim() || scoreLoading || submitLoading" @click="$emit('final-submit')">
                {{ submitLoading ? 'Submitting...' : experiment && experiment.mode !== 'classroom' ? 'Submit final and enter post-test' : 'Final submit' }}
              </button>
            </template>
            <div v-else class="submitted-lock-note">Final submitted. Editing is locked.</div>
          </div>
        </div>
      </main>
    </div>

    <div
      v-if="!experiment && !isReadonly && selectedText && !selectionToolbarDismissed"
      class="selection-toolbar"
      role="toolbar"
      aria-label="Selected text tools"
      :style="selectionToolbarStyle"
      @mousedown.stop
      @mouseup.stop
      @click.stop
    >
      <div class="toolbar-label">tools</div>
      <button @mousedown.stop.prevent @click="runSelectionPolish">improve writing</button>
      <div class="toolbar-divider"></div>
      <div class="toolbar-chat">
        <textarea v-model="selectionInstruction" placeholder="Ask about selected text..." @mousedown.stop @keydown.enter.exact.prevent="runSelectionChat" />
        <button class="btn-primary compact-btn" @mousedown.stop.prevent @click="runSelectionChat" :disabled="chatLoading || !selectionInstruction.trim()">Send</button>
      </div>
    </div>

    <button
      v-if="!isReadonly"
      type="button"
      class="floating-chat-button"
      :style="chatButtonStyle"
      @pointerdown="startChatDrag"
      @click="openChatButton"
      :aria-label="experiment ? 'Open reference library' : 'Open writing assistant'"
      title="Open assistant"
    >
      <img :src="avatarUrl" alt="assistant" />
    </button>
    <div v-if="showChatHint && !experiment" class="floating-chat-hint" :style="chatHintStyle">
      You can ask the AI Assistant for help while drafting.
    </div>

    <aside v-if="drawerOpen" class="assistant-drawer" :style="drawerStyle" aria-labelledby="writing-assistant-title">
        <div
          class="drawer-resize-handle"
          role="separator"
          tabindex="0"
          aria-label="Resize writing assistant"
          aria-orientation="vertical"
          :aria-valuemin="drawerMinWidth"
          :aria-valuemax="drawerMaxWidth"
          :aria-valuenow="effectiveDrawerWidth"
          @pointerdown="startDrawerResize"
          @keydown="handleDrawerResizeKeydown"
        ></div>
        <div class="drawer-header">
          <div>
            <h2 id="writing-assistant-title">Writing assistant</h2>
          </div>
          <button type="button" class="btn-icon" aria-label="Close writing assistant" @click="drawerOpen = false">×</button>
        </div>

        <div class="drawer-tabs" role="tablist" aria-label="Writing assistant views">
          <button v-if="!experiment" type="button" role="tab" :aria-selected="drawerTab === 'chat'" :class="drawerTab === 'chat' ? 'drawer-tab-active' : ''" @click="showChatTab">Chat</button>
          <button type="button" role="tab" :aria-selected="drawerTab === 'references'" :class="drawerTab === 'references' ? 'drawer-tab-active' : ''" @click="drawerTab = 'references'">References</button>
        </div>

        <div v-if="drawerTab === 'chat' && !experiment" class="drawer-chat">
          <div ref="drawerMessagesRef" class="drawer-messages">
            <div v-for="(msg, index) in store.messages" :key="index" :class="['drawer-message', msg.role === 'user' ? 'drawer-user' : 'drawer-assistant']">
              <div v-if="msg.role === 'assistant'" class="drawer-bubble assistant-output" v-html="formatAssistantMessage(msg.content)"></div>
              <div v-else-if="isToolMessage(msg)" class="drawer-bubble tool-user-bubble">
                <div class="tool-message-label">{{ toolMessageLabel(msg) }}</div>
                <blockquote v-if="msg.targetSentence">{{ msg.targetSentence }}</blockquote>
                <div class="tool-message-note">{{ toolMessageNote(msg) }}</div>
              </div>
              <div v-else class="drawer-bubble">{{ msg.content }}</div>
            </div>
            <div v-if="chatLoading" class="drawer-bubble loading-note">Thinking...</div>
          </div>
          <div class="drawer-composer">
            <div class="composer-tools">
              <button @click="runGeneralFeedback" :disabled="isReadonly || chatLoading || !store.combinedDraft.trim()">general feedback</button>
            </div>
            <textarea v-model="assistantInput" placeholder="Ask about the proposal..." :readonly="isReadonly" @keydown.enter.exact.prevent="notionSendAssistant()" />
            <button class="btn-primary" @click="notionSendAssistant()" :disabled="isReadonly || chatLoading || !assistantInput.trim()">Send</button>
          </div>
        </div>

        <div v-else class="reference-library">
          <div v-if="!store.references.length" class="reference-empty">No references yet. Add a source you found manually.</div>
          <article v-for="ref in store.references" :key="ref.id" class="library-card">
            <div class="library-card-top">
              <strong>{{ ref.title || 'Untitled reference' }}</strong>
              <div class="reference-card-tools">
                <button v-if="!isReadonly" class="icon-btn reference-edit-btn" @click="openReferenceEditor(ref)" title="Edit reference">✎</button>
                <button v-if="!isReadonly" class="icon-btn reference-delete-btn" @click="store.deleteReference(ref.id)" title="Delete reference">×</button>
              </div>
            </div>
            <div class="reference-apa">{{ ref.apa }}</div>
            <a
              v-if="referencePaperUrl(ref)"
              class="reference-open-link"
              :href="referencePaperUrl(ref)"
              target="_blank"
              rel="noopener noreferrer"
            >
              Open paper
            </a>
            <div v-if="ref.originalText" class="reference-note"><b>Original</b><span>{{ ref.originalText }}</span></div>
            <div v-if="ref.paraphrase" class="reference-note"><b>Paraphrase</b><span>{{ ref.paraphrase }}</span></div>
          </article>
          <button
            v-if="!isReadonly"
            class="add-reference-full"
            aria-label="Add reference"
            @click="openReferenceEditor()"
          >
            <span class="add-reference-icon" aria-hidden="true">+</span>
            Add reference
          </button>
        </div>
    </aside>

    <div v-if="manualReference" class="modal-overlay" @click.self="manualReference = null">
      <div class="modal reference-edit-modal">
        <div class="modal-header">
          <h2>{{ manualReference.id ? 'Edit Reference' : 'New Reference' }}</h2>
          <button class="btn-icon" @click="manualReference = null">×</button>
        </div>
        <div ref="referenceFormRef" class="modal-body reference-form">
          <label class="field-label">Article title</label>
          <input class="ref-input" v-model="manualReference.title" placeholder="Article title" />
          <div v-if="manualReference.id || experiment" class="field-label-row">
            <label class="field-label">APA reference</label>
            <button
              v-if="!experiment"
              class="btn-secondary mini-action-btn"
              @click="regenerateApaCitation"
              :disabled="apaRegenerating || referenceSaving || !manualReference.title.trim() || !manualReference.url.trim()"
            >
              {{ apaRegenerating ? 'Regenerating...' : 'Regenerate APA' }}
            </button>
          </div>
          <textarea v-if="manualReference.id || experiment" class="ref-input" v-model="manualReference.apa" rows="3" placeholder="APA reference" />
          <label class="field-label">Link or DOI URL</label>
          <input class="ref-input" v-model="manualReference.url" placeholder="https://doi.org/..." />
          <label class="field-label">Original text</label>
          <textarea class="ref-input" v-model="manualReference.originalText" rows="3" placeholder="Original wording from the source" />
          <label v-if="manualReference.id || experiment" class="field-label">Paraphrase (optional)</label>
          <textarea v-if="manualReference.id || experiment" class="ref-input" v-model="manualReference.paraphrase" rows="3" placeholder="Paraphrased sentence" />
          <button v-if="manualReference.id && !experiment" class="btn-secondary paraphrase-options-btn" @click="generateParaphraseOptions" :disabled="referenceSaving || !manualReference.originalText.trim()">
            {{ referenceSaving ? 'Generating...' : 'Paraphrase' }}
          </button>
          <div v-if="manualReference.paraphraseOptions?.length" class="paraphrase-options">
            <button
              v-for="option in manualReference.paraphraseOptions"
              :key="option"
              @click="manualReference.paraphrase = option"
            >
              {{ option }}
            </button>
          </div>
          <div v-if="referenceFormError" class="form-error">{{ referenceFormError }}</div>
        </div>
        <div class="modal-footer reference-modal-footer">
          <button class="btn-secondary" @click="manualReference = null" :disabled="referenceSaving">Cancel</button>
          <button class="btn-primary" @click="saveManualReference" :disabled="referenceSaving || !manualReferenceReady">
            {{ referenceSaving ? 'Completing...' : 'Save' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import avatarImg from '../assets/avatar.png'
import { apiUrl, apiFetch } from '../api'
import { experimentApi } from '../experiments/api'
import { normalizeTopicReply } from '../utils/topicAssistance.mjs'
import { logicalSubmit } from '../experiments/form-schema.mjs'
import { readAiJson } from '../aiResponse'
import { DRAFT_SECTIONS, useEssayStore } from '../stores/essay'

const props = defineProps({
  experiment: { type: Object, default: null },
  experimentBusy: Boolean,
  sectionPanelOpen: Boolean,
  scoreLoading: {
    type: Boolean,
    default: false,
  },
  saveLoading: {
    type: Boolean,
    default: false,
  },
  submitLoading: {
    type: Boolean,
    default: false,
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  aiUnavailable: {
    type: Boolean,
    default: false,
  },
  saveStatus: { type: String, default: '' },
})
const emit = defineEmits(['score', 'save', 'back', 'final-submit', 'section-improve', 'topic-input'])

const store = useEssayStore()
const avatarUrl = avatarImg
const draftSections = DRAFT_SECTIONS
const cefrLevels = ['B1', 'B2', 'C1']
const DRAWER_MIN_WIDTH = 340
const DRAWER_MAX_WIDTH = 760
const DRAWER_KEYBOARD_STEP = 24
const drawerOpen = ref(false)
const drawerTab = ref('chat')
const assistantInput = ref('')
const topicInput = ref(store.researchTopic)
const topicOptionsOpen = ref(true)
const topicFeedback = ref('')
const improvedTopicSuggestions = ref([])
const topicImproving = ref(false)
const topicAiSource = ref('ai')
const topicSourceLabel = computed(() => ({configured:'Assignment choices', assignment:'From the assignment', general:'General starting ideas'}[store.topicRecommendations.source]))
const draftStarting = ref(false)
const selectedText = ref('')
const selectionInstruction = ref('')
const selectionToolbarDismissed = ref(false)
const selectionTarget = ref(null)
const selectionToolbarPosition = ref({ left: 0, top: 0 })
const workspaceRef = ref(null)
const activeOutlineId = ref('prompt')
const chatButtonPosition = ref(null)
const chatDragState = ref(null)
const drawerMessagesRef = ref(null)
const referenceFormRef = ref(null)
const drawerWidth = ref(460)
const drawerViewportWidth = ref(window.innerWidth)
const drawerResizeState = ref(null)
const chatLoading = ref(false)
const manualReference = ref(null)
const referenceFormError = ref('')
const referenceSaving = ref(false)
const apaRegenerating = ref(false)
const showChatHint = ref(false)
const paraphraseRequestCount = ref(0)
const aiUnavailableNotice = ref(false)
let chatHintTimer = null
let topicOperation = logicalSubmit()

async function studyTopicRequest(payload) {
  const path = `participants/${props.experiment.id}/topics`
  try { return await topicOperation.run(path, payload, request_id => experimentApi(path, {...payload, request_id})) }
  catch (error) {
    // A network failure replays the same logical request. A recorded provider
    // failure permits an explicit new attempt; pending work keeps its ID.
    if (error.status && error.code !== 'topic_pending') topicOperation = logicalSubmit()
    throw error
  }
}

const isReadonly = computed(() => props.readonly)
const hasTeacherReview = computed(() => store.teacherScore !== null || Boolean(store.teacherFeedback))
const completedSections = computed(() => draftSections.filter(section => (
  String(store.draftSections[section.key] || '').trim()
)).length)
const draftProgress = computed(() => Math.round((completedSections.value / draftSections.length) * 100))
const showAiUnavailable = computed(() => props.aiUnavailable || aiUnavailableNotice.value)

const outlineItems = computed(() => [
  { id: 'prompt', label: 'Prompt' },
  ...(isReadonly.value && store.scoreResult ? [{ id: 'submitted-score', label: 'AI Score' }] : []),
  ...(isReadonly.value && hasTeacherReview.value ? [{ id: 'teacher-review', label: 'Teacher Review' }] : []),
  { id: 'topic-selection', label: 'Topic' },
  ...DRAFT_SECTIONS.map(section => ({ id: section.key, label: section.label })),
])

const selectionToolbarStyle = computed(() => ({
  left: `${selectionToolbarPosition.value.left}px`,
  top: `${selectionToolbarPosition.value.top}px`,
}))

const chatButtonStyle = computed(() => {
  if (!chatButtonPosition.value) return {}
  return {
    left: `${chatButtonPosition.value.left}px`,
    top: `${chatButtonPosition.value.top}px`,
    right: 'auto',
    bottom: 'auto',
  }
})

const chatHintStyle = computed(() => {
  if (!chatButtonPosition.value) return {}
  return {
    left: `${Math.max(8, chatButtonPosition.value.left - 254)}px`,
    top: `${chatButtonPosition.value.top + 8}px`,
    right: 'auto',
    bottom: 'auto',
  }
})

const displayedTopicSuggestions = computed(() => store.topicExamples)

const manualReferenceReady = computed(() => (
  Boolean(manualReference.value?.title.trim() && manualReference.value?.url.trim() && manualReference.value?.originalText.trim())
))

const drawerMaxWidth = computed(() => Math.max(0, Math.min(drawerViewportWidth.value - 160, DRAWER_MAX_WIDTH)))
const drawerMinWidth = computed(() => Math.min(DRAWER_MIN_WIDTH, drawerMaxWidth.value))
const effectiveDrawerWidth = computed(() => clampDrawerWidth(drawerWidth.value))

const drawerStyle = computed(() => ({
  width: `${effectiveDrawerWidth.value}px`,
}))

const workspaceStyle = computed(() => {
  if (!drawerOpen.value) return {}
  return {
    '--drawer-reservation': `${effectiveDrawerWidth.value + 24}px`,
  }
})

onMounted(() => {
  updateActiveOutline()
  document.addEventListener('pointermove', handleChatDrag)
  document.addEventListener('pointerup', stopChatDrag)
  document.addEventListener('pointermove', handleDrawerResize)
  document.addEventListener('pointerup', stopDrawerResize)
  window.addEventListener('resize', updateDrawerViewportWidth)
})

onBeforeUnmount(() => {
  document.removeEventListener('pointermove', handleChatDrag)
  document.removeEventListener('pointerup', stopChatDrag)
  document.removeEventListener('pointermove', handleDrawerResize)
  document.removeEventListener('pointerup', stopDrawerResize)
  window.removeEventListener('resize', updateDrawerViewportWidth)
  if (chatHintTimer) clearTimeout(chatHintTimer)
})

watch([drawerOpen, drawerWidth], () => {
  scheduleDraftEditorResize()
})

watch(drawerViewportWidth, () => {
  scheduleDraftEditorResize()
})

watch(() => store.researchTopic, (topic) => {
  if (store.stage === 'drafting') topicInput.value = topic || topicInput.value
})
watch(topicInput, value => emit('topic-input', value))
watch(() => props.experiment?.id, async id => {
  if (!id) return
  try {
    const data = await experimentApi(`participants/${id}/topics`)
    if (props.experiment?.id === id && Array.isArray(data.topics) && data.topics.length) {
      store.configuredTopics = data.topics
      store.configuredTopicsSource = data.source
    }
  } catch { /* Local fallback remains available while recommendations reload. */ }
}, {immediate:true})

function sectionMeta(section) {
  const guidance = store.sectionGuidance[section.key] || {}
  return {
    ...section,
    framework: guidance.framework || section.framework || [],
    hintQuestions: guidance.questions || section.hintQuestions || [],
  }
}

function scrollTo(id) {
  const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  document.getElementById(id)?.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' })
}

function markAiUnavailable() {
  aiUnavailableNotice.value = true
}

function buildTopicGuidance(topic) {
  const focusedTopic = topic || 'your selected topic'
  return {
    background: {
      framework: DRAFT_SECTIONS.find(section => section.key === 'background')?.framework || [],
      questions: [
        `What current situation or trend makes ${focusedTopic} worth studying now?`,
        `What evidence or source could help explain the background of ${focusedTopic}?`,
        `What response, practice, policy, or behaviour is connected to ${focusedTopic}?`,
      ],
    },
    objective: {
      framework: DRAFT_SECTIONS.find(section => section.key === 'objective')?.framework || [],
      questions: [
        `What attitudes or views about ${focusedTopic} will your study explore?`,
        `Who is the target audience or participant group for ${focusedTopic}?`,
        `How could findings about ${focusedTopic} be useful to teachers, institutions, workplaces, or practitioners?`,
      ],
    },
    methodology: {
      framework: DRAFT_SECTIONS.find(section => section.key === 'methodology')?.framework || [],
      questions: [
        `When will you collect data for the study on ${focusedTopic}?`,
        `How many participants will you invite, and what group should they come from?`,
        `Which method will best collect views on ${focusedTopic}: questionnaire, interview, observation, or another method?`,
      ],
    },
  }
}

function clearTopicImprovement() {
  topicFeedback.value = ''
  improvedTopicSuggestions.value = []
}

function beginTopicEdit() {
  topicInput.value = store.researchTopic || topicInput.value
  topicFeedback.value = ''
  improvedTopicSuggestions.value = []
  topicOptionsOpen.value = false
  store.stage = 'topic'
}

function chooseTopic(topic) {
  if (props.readonly || draftStarting.value) return
  topicInput.value = topic
}

async function improveTopicIdea() {
  if (props.readonly) return
  const idea = topicInput.value.trim()
  if (idea.length > 500) { topicFeedback.value = 'Enter a research topic of at most 500 characters.'; return }
  if (!idea || topicImproving.value) return
  topicImproving.value = true
  topicFeedback.value = ''
  improvedTopicSuggestions.value = []
  try {
    let data
    if (props.experiment) {
      data = await studyTopicRequest({ action: 'improve', idea })
    } else {
    const res = await apiFetch('chat', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        mode: 'improve_topic',
        topic: store.assignmentPrompt,
        cefr_level: store.cefrLevel,
        idea,
        assignment_id: store.assignmentId,
      }),
    })
    data = await readAiJson(res, markAiUnavailable)
    }
    if (topicInput.value.trim() !== idea || store.stage === 'drafting') return
    const reply = normalizeTopicReply(data)
    topicFeedback.value = reply.feedback
    improvedTopicSuggestions.value = reply.topics
    topicAiSource.value = data.source || 'ai'
    topicOptionsOpen.value = true
  } catch (error) {
    topicFeedback.value = `${error.message || 'Could not improve this topic right now.'} Your input is unchanged. Use AI improve to retry, edit the topic, or start your draft.`
  } finally {
    topicImproving.value = false
  }
}

function updateActiveOutline() {
  const workspace = workspaceRef.value
  if (!workspace) return
  const topBoundary = workspace.getBoundingClientRect().top + 80
  let current = outlineItems.value[0]?.id || 'prompt'
  for (const item of outlineItems.value) {
    const section = document.getElementById(item.id)
    if (section && section.getBoundingClientRect().top <= topBoundary) {
      current = item.id
    }
  }
  activeOutlineId.value = current
}

function startChatDrag(event) {
  const rect = event.currentTarget.getBoundingClientRect()
  chatDragState.value = {
    startX: event.clientX,
    startY: event.clientY,
    left: rect.left,
    top: rect.top,
    moved: false,
  }
  event.currentTarget.setPointerCapture?.(event.pointerId)
}

function handleChatDrag(event) {
  if (!chatDragState.value) return
  const dx = event.clientX - chatDragState.value.startX
  const dy = event.clientY - chatDragState.value.startY
  if (Math.abs(dx) + Math.abs(dy) > 4) chatDragState.value.moved = true
  const maxLeft = window.innerWidth - 68
  const maxTop = window.innerHeight - 68
  chatButtonPosition.value = {
    left: Math.min(Math.max(8, chatDragState.value.left + dx), maxLeft),
    top: Math.min(Math.max(8, chatDragState.value.top + dy), maxTop),
  }
}

function stopChatDrag() {
  if (!chatDragState.value) return
  setTimeout(() => {
    chatDragState.value = null
  }, 0)
}

function openChatButton() {
  if (props.experiment) { drawerTab.value = 'references'; drawerOpen.value = !drawerOpen.value; return }
  if (props.readonly) return
  if (chatDragState.value?.moved) return
  drawerOpen.value = true
  showChatTab()
}

function resizeDraftEditor(event) {
  const editor = event?.target
  if (!editor || editor.tagName !== 'TEXTAREA') return
  editor.style.height = 'auto'
  editor.style.height = `${Math.max(editor.scrollHeight, 220)}px`
}

async function resizeAllDraftEditors() {
  await nextTick()
  document.querySelectorAll('textarea.notion-editor').forEach(editor => {
    editor.style.height = 'auto'
    editor.style.height = `${Math.max(editor.scrollHeight, 220)}px`
  })
}

function scheduleDraftEditorResize() {
  resizeAllDraftEditors()
  window.requestAnimationFrame?.(() => resizeAllDraftEditors())
  setTimeout(() => resizeAllDraftEditors(), 260)
}

function showChatTab() {
  drawerTab.value = 'chat'
  scrollMessagesToBottom()
}

function updateDrawerViewportWidth() {
  drawerViewportWidth.value = window.innerWidth
}

function clampDrawerWidth(width) {
  return Math.min(Math.max(drawerMinWidth.value, width), drawerMaxWidth.value)
}

function setDrawerWidth(width) {
  drawerWidth.value = clampDrawerWidth(width)
}

function startDrawerResize(event) {
  drawerResizeState.value = {
    startX: event.clientX,
    width: effectiveDrawerWidth.value,
  }
  event.preventDefault()
}

function handleDrawerResize(event) {
  if (!drawerResizeState.value) return
  const dx = drawerResizeState.value.startX - event.clientX
  setDrawerWidth(drawerResizeState.value.width + dx)
}

function handleDrawerResizeKeydown(event) {
  let nextWidth = effectiveDrawerWidth.value
  if (event.key === 'ArrowLeft') nextWidth += DRAWER_KEYBOARD_STEP
  else if (event.key === 'ArrowRight') nextWidth -= DRAWER_KEYBOARD_STEP
  else if (event.key === 'Home') nextWidth = drawerMinWidth.value
  else if (event.key === 'End') nextWidth = drawerMaxWidth.value
  else return
  event.preventDefault()
  setDrawerWidth(nextWidth)
}

function stopDrawerResize() {
  drawerResizeState.value = null
}

async function scrollMessagesToBottom() {
  await nextTick()
  if (drawerTab.value !== 'chat') return
  const el = drawerMessagesRef.value
  if (el) el.scrollTop = el.scrollHeight
}

function emptyReference() {
  referenceFormError.value = ''
  return { id: '', title: '', apa: '', originalText: '', paraphrase: '', paraphraseOptions: [], url: '', doi: '' }
}

function openReferenceEditor(reference = null) {
  if (props.readonly) return
  referenceFormError.value = ''
  paraphraseRequestCount.value = 0
  manualReference.value = reference
    ? {
        id: reference.id,
        title: reference.title || '',
        apa: reference.apa || '',
        originalText: reference.originalText || '',
        paraphrase: reference.paraphrase || '',
        paraphraseOptions: [...(reference.paraphraseOptions || [])],
        url: reference.url || '',
        doi: reference.doi || '',
      }
    : emptyReference()
}

function captureSelection(event) {
  if (props.experiment) return
  if (props.readonly) return
  const target = event.target
  if (!target) return

  if (target.tagName === 'TEXTAREA') {
    const value = target.value || ''
    const start = target.selectionStart || 0
    const end = target.selectionEnd || 0
    selectedText.value = start !== end ? value.slice(start, end).trim() : ''
    selectionTarget.value = selectedText.value ? target : null
    selectionToolbarDismissed.value = !selectedText.value
    if (selectedText.value) positionToolbarAtTextareaSelectionEnd(target, end)
    return
  }

  if (target.isContentEditable) {
    selectedText.value = window.getSelection()?.toString().trim() || ''
    selectionTarget.value = selectedText.value ? target : null
    selectionToolbarDismissed.value = !selectedText.value
    if (selectedText.value) positionToolbarAtSelectionEnd()
    return
  }

  selectedText.value = ''
  selectionTarget.value = null
  selectionToolbarDismissed.value = true
}

function clampToolbarPosition(left, top) {
  selectionToolbarPosition.value = {
    left: Math.min(Math.max(left, 12), window.innerWidth - 300),
    top: Math.min(Math.max(top, 12), window.innerHeight - 220),
  }
}

function positionToolbarAtTextareaSelectionEnd(textarea, end) {
  const marker = document.createElement('span')
  const mirror = document.createElement('div')
  const style = window.getComputedStyle(textarea)
  const properties = [
    'boxSizing', 'width', 'fontFamily', 'fontSize', 'fontWeight', 'lineHeight',
    'letterSpacing', 'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft',
    'borderTopWidth', 'borderRightWidth', 'borderBottomWidth', 'borderLeftWidth',
    'whiteSpace', 'wordBreak', 'overflowWrap',
  ]
  properties.forEach(prop => {
    mirror.style[prop] = style[prop]
  })
  mirror.style.position = 'absolute'
  mirror.style.visibility = 'hidden'
  mirror.style.left = '-9999px'
  mirror.style.top = '0'
  mirror.style.height = 'auto'
  mirror.style.whiteSpace = 'pre-wrap'
  mirror.style.overflowWrap = 'break-word'
  mirror.textContent = textarea.value.slice(0, end)
  marker.textContent = '\u200b'
  mirror.appendChild(marker)
  document.body.appendChild(mirror)
  const textRect = textarea.getBoundingClientRect()
  const markerRect = marker.getBoundingClientRect()
  const mirrorRect = mirror.getBoundingClientRect()
  const left = textRect.left + markerRect.left - mirrorRect.left - textarea.scrollLeft + 8
  const top = textRect.top + markerRect.top - mirrorRect.top - textarea.scrollTop + parseFloat(style.lineHeight || 24)
  document.body.removeChild(mirror)
  clampToolbarPosition(left, top)
}

function positionToolbarAtSelectionEnd() {
  const selection = window.getSelection()
  if (!selection || !selection.rangeCount) return
  const range = selection.getRangeAt(0).cloneRange()
  range.collapse(false)
  const rect = range.getBoundingClientRect()
  if (rect.width || rect.height) {
    clampToolbarPosition(rect.right + 8, rect.bottom + 8)
    return
  }
  const fallback = selection.getRangeAt(0).getBoundingClientRect()
  clampToolbarPosition(fallback.right + 8, fallback.bottom + 8)
}

async function notionStartDrafting() {
  if (props.readonly) return
  if (topicInput.value.trim().length > 500) { topicFeedback.value = 'Enter a research topic of at most 500 characters.'; return }
  if (!topicInput.value.trim()) return
  if (props.experiment) {
    if (draftStarting.value) return
    draftStarting.value = true
    const selectedTopic = topicInput.value.trim()
    try {
      await studyTopicRequest({action:'select', topic:selectedTopic})
      store.confirmTopic(selectedTopic, {})
      await nextTick(); scrollTo('background'); scheduleDraftEditorResize()
    } catch (error) { topicFeedback.value = `${error.message} Your topic and writing are unchanged. Try Start draft again.` }
    finally { draftStarting.value = false }
    return
  }
  const topic = topicInput.value.trim()
  draftStarting.value = true
  try {
    const res = await apiFetch('chat', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        mode: 'confirm_topic',
        assignment_id: store.assignmentId,
        topic: store.assignmentPrompt,
        research_topic: topic,
        cefr_level: store.cefrLevel,
        messages: [{ role: 'user', content: topic }],
      }),
    })
    const data = await readAiJson(res, markAiUnavailable)
    const confirmedTopic = data.research_topic || topic
    store.confirmTopic(confirmedTopic, data.section_guidance || buildTopicGuidance(confirmedTopic))
    topicOptionsOpen.value = false
    topicFeedback.value = ''
    await nextTick()
    scrollTo('background')
    scheduleDraftEditorResize()
    showChatInteractionHint()
  } catch {
    markAiUnavailable()
    store.confirmTopic(topic, buildTopicGuidance(topic))
    await nextTick()
    scrollTo('background')
    scheduleDraftEditorResize()
    showChatInteractionHint()
  } finally {
    draftStarting.value = false
  }
}

function showChatInteractionHint() {
  showChatHint.value = true
  if (chatHintTimer) clearTimeout(chatHintTimer)
  chatHintTimer = setTimeout(() => {
    showChatHint.value = false
    chatHintTimer = null
  }, 4200)
}

async function notionSendAssistant(override = null) {
  if (props.experiment) return
  if (props.readonly) return
  const instruction = override?.instruction || assistantInput.value.trim()
  if (!instruction || chatLoading.value) return
  const mode = override?.mode || 'chat'
  store.messages.push({
    role: 'user',
    mode,
    content: override?.label || instruction,
    targetSentence: override?.targetSentence || '',
    tool: override?.tool || '',
  })
  assistantInput.value = ''
  drawerOpen.value = true
  drawerTab.value = mode === 'references' ? 'references' : 'chat'
  selectedText.value = ''
  chatLoading.value = true
  scrollMessagesToBottom()
  try {
    const res = await fetch(apiUrl('chat'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        mode,
        target_sentence: override?.targetSentence || '',
        instruction,
        general: override?.general || false,
        topic: store.topic,
        research_topic: store.researchTopic || topicInput.value,
        cefr_level: store.cefrLevel,
        stage: store.stage,
        active_section: store.activeDraftSection,
        section_guidance: store.sectionGuidance,
        draft_sections: store.draftSections,
        draft: store.combinedDraft,
        references: store.references,
        messages: store.messages.map(m => ({ role: m.role, content: m.content, mode: m.mode })),
      }),
    })
    const data = await readAiJson(res, markAiUnavailable)
    if (Array.isArray(data.references)) data.references.forEach(ref => store.addReference(ref))
    store.messages.push({ role: 'assistant', mode: data.mode || mode, content: data.reply || 'No response.' })
    await scrollMessagesToBottom()
    if (mode === 'references' || data.mode === 'references') {
      drawerTab.value = 'references'
    }
  } catch {
    markAiUnavailable()
    store.messages.push({ role: 'assistant', mode, content: 'Network error. Please try again.' })
    await scrollMessagesToBottom()
  } finally {
    chatLoading.value = false
  }
}

function runSelectionPolish() {
  if (props.readonly) return
  const targetSentence = selectedText.value
  if (!targetSentence) return
  collapseSelectionTarget()
  selectionToolbarDismissed.value = true
  selectedText.value = ''
  notionSendAssistant({
    mode: 'polish',
    targetSentence,
    label: targetSentence,
    tool: 'improve writing',
    instruction: 'Improve writing for the selected sentence.',
  })
}

function runSelectionChat() {
  if (props.readonly) return
  const targetSentence = selectedText.value
  const instruction = selectionInstruction.value.trim()
  if (!targetSentence || !instruction) return
  collapseSelectionTarget()
  selectionToolbarDismissed.value = true
  selectedText.value = ''
  notionSendAssistant({
    mode: 'chat',
    targetSentence,
    label: instruction,
    tool: 'selected chat',
    instruction: `Selected text: ${targetSentence}\n\nStudent request: ${instruction}`,
  })
  selectionInstruction.value = ''
}

function runGeneralFeedback() {
  if (props.readonly) return
  notionSendAssistant({
    mode: 'feedback',
    label: 'General feedback',
    tool: 'general feedback',
    instruction: 'Give general feedback on the current full draft.',
    general: true,
  })
}

function isToolMessage(msg) {
  return ['polish', 'references', 'feedback'].includes(msg.mode) || Boolean(msg.tool || msg.targetSentence)
}

function toolMessageLabel(msg) {
  return msg.tool || (msg.mode === 'references' ? 'find reference' : msg.mode === 'polish' ? 'improve writing' : 'general feedback')
}

function toolMessageNote(msg) {
  if (msg.mode === 'references') return msg.targetSentence ? 'Finding references for this selected text.' : 'Finding references for the full draft.'
  if (msg.mode === 'polish') return 'Improving the selected writing.'
  if (msg.mode === 'feedback') return 'Giving feedback on the full draft.'
  if (msg.tool === 'selected chat') return msg.content
  return 'Using the selected text as context.'
}

function collapseSelectionTarget() {
  const target = selectionTarget.value
  if (target?.tagName === 'TEXTAREA') {
    const end = target.selectionEnd || target.selectionStart || 0
    target.setSelectionRange(end, end)
  } else if (target?.isContentEditable) {
    window.getSelection()?.removeAllRanges()
  }
  selectionTarget.value = null
}

function generateReferencesSection() {
  if (props.readonly) return
  if (props.experiment) { store.draftSections.references = store.references.map(ref => ref.apa || [ref.title, ref.url].filter(Boolean).join('. ')).filter(Boolean).join('\n\n'); return }
  store.draftSections.references = store.references
    .map(ref => ref.apa || [ref.title, ref.url].filter(Boolean).join('. '))
    .filter(Boolean)
    .map(text => `<p class="reference-entry">${escapeHtml(text)}</p>`)
    .join('')
}

function updateReferencesDraft(event) {
  if (props.readonly) return
  store.draftSections.references = event.target.innerHTML
}

function escapeHtml(value = '') {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')
}

function referencePaperUrl(ref = {}) {
  return ref.paperUrl || ref.paper_url || ref.url || ''
}

function formatAssistantMessage(content = '') {
  const escaped = escapeHtml(content)
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/(?:^|\n)- (.+)/g, '<br><span class="assistant-bullet">• $1</span>')
    .replace(/\n/g, '<br>')
  return escaped.replace(/^<br>/, '')
}

async function saveManualReference() {
  if (props.readonly) return
  if (!manualReference.value) return
  referenceFormError.value = ''
  const payload = {
    id: manualReference.value.id,
    title: manualReference.value.title.trim(),
    apa: manualReference.value.apa.trim(),
    originalText: manualReference.value.originalText.trim(),
    paraphrase: manualReference.value.paraphrase.trim(),
    paraphraseOptions: manualReference.value.paraphraseOptions || [],
    url: manualReference.value.url.trim(),
    doi: manualReference.value.doi.trim(),
  }
  if (!payload.title || !payload.originalText || !payload.url) {
    referenceFormError.value = 'Please fill in article title, original text, and link or DOI URL.'
    return
  }
  referenceSaving.value = true
  try {
    const completed = payload.id || props.experiment
      ? payload
      : await completeReferencePayload(payload)
    if (!completed.apa || (!props.experiment && !completed.paraphrase)) {
      referenceFormError.value = 'Citation and paraphrase could not be completed. Please check the source details and try again.'
      return
    }
    if (payload.id) {
      store.updateReference(payload.id, completed)
    } else {
      store.addReference(completed)
    }
    manualReference.value = null
  } catch {
    markAiUnavailable()
    referenceFormError.value = 'Could not auto-complete this reference. Please try again later.'
  } finally {
    referenceSaving.value = false
  }
}

async function regenerateApaCitation() {
  if (props.readonly) return
  if (!manualReference.value) return
  referenceFormError.value = ''
  apaRegenerating.value = true
  try {
    const completed = await completeReferencePayload({
      ...manualReference.value,
      title: manualReference.value.title.trim(),
      apa: manualReference.value.apa.trim(),
      originalText: manualReference.value.originalText.trim(),
      paraphrase: manualReference.value.paraphrase.trim(),
      url: manualReference.value.url.trim(),
      doi: manualReference.value.doi.trim(),
    })
    if (!completed.apa) {
      referenceFormError.value = 'Could not regenerate the APA reference. Please check the title and link or DOI.'
      return
    }
    manualReference.value.apa = completed.apa
    if (!manualReference.value.paraphrase && completed.paraphrase) {
      manualReference.value.paraphrase = completed.paraphrase
    }
  } catch {
    markAiUnavailable()
    referenceFormError.value = 'Could not regenerate the APA reference. Please try again later.'
  } finally {
    apaRegenerating.value = false
  }
}

async function completeReferencePayload(reference) {
  const res = await fetch(apiUrl('chat'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      mode: 'complete_reference',
      topic: store.topic,
      research_topic: store.researchTopic,
      cefr_level: store.cefrLevel,
      reference,
      references: store.references,
    }),
  })
  const data = await readAiJson(res, markAiUnavailable)
  return {
    ...reference,
    ...(data.reference || {}),
    apa: data.reference?.apa || '',
    paraphrase: data.reference?.paraphrase || '',
    paraphraseOptions: data.reference?.paraphraseOptions || data.reference?.paraphrase_options || [],
  }
}

async function generateParaphraseOptions() {
  if (props.readonly) return
  if (!manualReference.value) return
  referenceFormError.value = ''
  referenceSaving.value = true
  const previousOptions = [...(manualReference.value.paraphraseOptions || [])]
  paraphraseRequestCount.value += 1
  try {
    const res = await fetch(apiUrl('chat'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        mode: 'paraphrase_options',
        topic: store.topic,
        research_topic: store.researchTopic,
        cefr_level: store.cefrLevel,
        reference: manualReference.value,
        previous_options: previousOptions,
        variation_seed: paraphraseRequestCount.value,
      }),
    })
    const data = await readAiJson(res, markAiUnavailable)
    manualReference.value.paraphraseOptions = (data.options || []).slice(0, 3)
    if (!manualReference.value.paraphraseOptions.length) {
      referenceFormError.value = 'Could not generate paraphrase options. Please try again.'
    } else {
      await nextTick()
      if (referenceFormRef.value) referenceFormRef.value.scrollTop = referenceFormRef.value.scrollHeight
    }
  } catch {
    markAiUnavailable()
    referenceFormError.value = 'Could not generate paraphrase options. Please try again.'
  } finally {
    referenceSaving.value = false
  }
}
</script>

<style scoped>
.section-improve-action { display: flex; justify-content: flex-end; margin-top: 12px; }
.section-panel-layout .workspace-topbar { grid-template-columns: minmax(0, 1fr); gap: 12px; }
.section-panel-layout .draft-progress { display: none; }
.section-panel-layout .workspace-body { grid-template-columns: minmax(0, 820px); }
.section-panel-layout .document-outline { display: none; }
.notion-workspace {
  --workspace-canvas: #ece9e1;
  --paper: #fbfaf6;
  --paper-muted: #f2efe8;
  --paper-edge: #d8d2c8;
  --paper-ink: #26241f;
  --paper-muted-ink: #716c63;
  position: relative;
  flex: 1;
  min-height: 0;
  height: 100%;
  overflow-y: auto;
  background: var(--workspace-canvas);
  color: var(--paper-ink);
  transition: padding-right 180ms ease;
}

.drawer-open-workspace {
  padding-right: min(var(--drawer-reservation), calc(100vw - 160px));
}

.workspace-topbar {
  position: sticky;
  top: 0;
  z-index: 35;
  display: grid;
  grid-template-columns: minmax(240px, 1fr) minmax(180px, 240px);
  align-items: center;
  gap: var(--space-6);
  min-height: 72px;
  padding: 10px clamp(18px, 3vw, 42px);
  border-bottom: 1px solid var(--paper-edge);
  background: rgba(251, 250, 246, 0.97);
}

.workspace-topbar__identity {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  min-width: 0;
}

.workspace-document-title {
  display: grid;
  min-width: 0;
  line-height: 1.2;
}

.workspace-document-title span,
.draft-progress__summary span,
.document-outline__heading span,
.paper-masthead {
  color: var(--paper-muted-ink);
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.workspace-document-title strong {
  overflow: hidden;
  color: var(--paper-ink);
  font-family: var(--font-academic);
  font-size: 1rem;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.draft-progress {
  display: grid;
  gap: 4px;
  min-width: 0;
}

.draft-progress__summary {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--space-2);
}

.draft-progress__summary strong {
  color: var(--accent-strong);
  font-size: 0.78rem;
}

.draft-progress__track {
  height: 3px;
  overflow: hidden;
  background: var(--paper-edge);
}

.draft-progress__track span {
  display: block;
  height: 100%;
  background: var(--accent);
  transition: width 180ms ease;
}

.draft-progress small {
  overflow: hidden;
  color: var(--paper-muted-ink);
  font-size: 0.68rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.submit-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--space-2);
  margin-top: var(--space-8);
  padding-top: var(--space-6);
  border-top: 1px solid var(--paper-edge);
}

.save-status { flex: 1 1 180px; color: var(--paper-muted-ink); font-size: 0.75rem; line-height: 1.4; }
.workspace-actions { flex-wrap: wrap; }

.submitted-lock-note {
  padding: 7px 10px;
  border: 1px solid var(--paper-edge);
  border-radius: var(--radius-control);
  background: var(--paper-muted);
  color: var(--paper-muted-ink);
  font-size: 0.78rem;
}

.workspace-body {
  display: grid;
  grid-template-columns: 164px minmax(0, 820px);
  align-items: start;
  justify-content: center;
  gap: clamp(24px, 3vw, 42px);
  width: min(1280px, 100%);
  margin: 0 auto;
  padding: clamp(24px, 4vw, 48px) clamp(18px, 3vw, 42px) 88px;
}

.drawer-open-workspace .workspace-body {
  justify-content: start;
}

.notion-page {
  width: 100%;
  min-width: 0;
}

.document-outline {
  position: sticky;
  top: 104px;
  align-self: start;
  padding: 2px 0 0 18px;
}

.document-outline::before {
  position: absolute;
  top: 48px;
  bottom: 56px;
  left: 5px;
  width: 1px;
  background: var(--paper-edge);
  content: "";
}

.document-outline__heading {
  display: grid;
  margin-bottom: var(--space-3);
  line-height: 1.1;
}

.document-outline__heading strong {
  color: var(--paper-ink);
  font-family: var(--font-academic);
  font-size: 1.18rem;
  font-weight: 600;
}

.document-outline nav {
  display: grid;
  gap: 2px;
}

.page-outline button {
  position: relative;
  display: grid;
  grid-template-columns: 12px minmax(0, 1fr);
  align-items: center;
  gap: 8px;
  width: 100%;
  min-height: 34px;
  border: 0;
  border-radius: var(--radius-control);
  padding: 5px 7px 5px 0;
  background: transparent;
  color: var(--paper-muted-ink);
  font-size: 0.75rem;
  text-align: left;
}

.page-outline button:hover {
  color: var(--paper-ink);
}

.outline-marker {
  z-index: 1;
  width: 7px;
  height: 7px;
  border: 1px solid #aaa399;
  border-radius: 50%;
  background: var(--workspace-canvas);
}

.page-outline .outline-active {
  color: var(--accent-strong);
  font-weight: 700;
}

.page-outline .outline-active .outline-marker {
  border-color: var(--accent);
  background: var(--accent);
}

.document-outline p {
  margin: var(--space-4) 0 0;
  color: var(--paper-muted-ink);
  font-size: 0.7rem;
  line-height: 1.5;
}

.ai-unavailable-notice {
  display: grid;
  gap: 2px;
  margin-bottom: var(--space-4);
  border-color: #d4a792;
  border-left: 3px solid var(--accent);
  border-radius: 0 var(--radius-surface) var(--radius-surface) 0;
  background: #f6eee8;
  font-size: 0.82rem;
}

.ai-unavailable-notice span {
  color: var(--paper-muted-ink);
}

.writing-paper {
  min-height: calc(100vh - 176px);
  padding: clamp(32px, 5vw, 64px);
  border: 1px solid var(--paper-edge);
  background: var(--paper);
}

.paper-masthead {
  display: flex;
  justify-content: space-between;
  gap: var(--space-4);
  margin-bottom: clamp(32px, 5vw, 56px);
  padding-bottom: var(--space-3);
  border-bottom: 3px double var(--paper-edge);
}

.notion-section {
  scroll-margin-top: 100px;
  margin-bottom: clamp(30px, 5vw, 48px);
}

.section-kicker {
  margin-bottom: 9px;
  color: var(--accent-strong);
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.09em;
  text-transform: uppercase;
}

.prompt-box {
  max-height: 190px;
  overflow-y: auto;
  padding: 16px 18px;
  border-left: 2px solid var(--paper-edge);
  background: var(--paper-muted);
  color: #514d46;
  font-family: var(--font-academic);
  font-size: 0.98rem;
  line-height: 1.68;
  white-space: pre-wrap;
}

.submitted-score-card {
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: var(--space-6);
  padding: var(--space-6) 0;
  border-top: 1px solid var(--paper-edge);
  border-bottom: 1px solid var(--paper-edge);
}

.teacher-review-card {
  background: #fdfbf5;
}

.submitted-score-main {
  color: var(--paper-ink);
  font-family: var(--font-academic);
  font-size: 2rem;
}

.submitted-score-details {
  display: grid;
  gap: 10px;
}

.submitted-score-dimension {
  display: grid;
  gap: 4px;
  padding-bottom: 10px;
  border-bottom: 1px solid var(--paper-edge);
}

.submitted-score-dimension strong {
  color: var(--accent-strong);
  font-size: 0.8rem;
}

.submitted-score-dimension span,
.submitted-score-summary {
  color: #514d46;
  font-size: 0.88rem;
  line-height: 1.55;
}

.submitted-score-summary {
  font-style: italic;
}

.topic-card {
  padding: clamp(18px, 3vw, 26px);
  border: 1px solid var(--paper-edge);
  background: #fdfbf6;
}

.topic-card h2 {
  margin: 0;
  color: var(--paper-ink);
  font-family: var(--font-academic);
  font-size: clamp(1.35rem, 3vw, 1.7rem);
  font-weight: 600;
  line-height: 1.25;
}

.topic-title-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 4px;
}

.topic-meta-row {
  display: flex;
  align-items: center;
  margin-top: var(--space-3);
}

.confirmed-topic {
  flex: 1;
  min-width: 0;
  padding: 10px 12px;
  border-left: 2px solid var(--accent);
  background: var(--paper-muted);
  color: #3d3933;
  font-family: var(--font-academic);
  font-size: 1rem;
  line-height: 1.45;
}

.cefr-control {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 3px;
  border: 1px solid var(--paper-edge);
  border-radius: var(--radius-control);
  background: var(--paper-muted);
}

.cefr-control > span {
  padding: 0 7px;
  color: var(--paper-muted-ink);
  font-size: 0.68rem;
  font-weight: 800;
}

.cefr-control button {
  min-width: 38px;
  height: 30px;
  border: 0;
  border-radius: 4px;
  padding: 0 8px;
  background: transparent;
  color: #514d46;
  font-size: 0.75rem;
}

.cefr-control .cefr-active {
  border: 1px solid var(--paper-edge);
  background: var(--paper);
  color: var(--accent-strong);
  font-weight: 800;
}

.cefr-control button:disabled {
  opacity: 0.65;
}

.topic-edit-btn {
  flex: 0 0 auto;
}

.topic-options {
  margin-top: var(--space-4);
}

.topic-options summary,
.hint-details summary {
  color: var(--accent-strong);
  font-size: 0.8rem;
  font-weight: 700;
  cursor: pointer;
}

.topic-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--space-2);
  margin-top: 10px;
}

.topic-chip {
  border: 1px solid var(--paper-edge);
  border-radius: var(--radius-control);
  padding: 10px 12px;
  background: var(--paper);
  color: #514d46;
  line-height: 1.4;
  text-align: left;
}

.topic-chip:hover:not(:disabled) {
  border-color: #c8a391;
  background: var(--accent-soft);
}

.topic-entry {
  display: flex;
  gap: 10px;
  margin-top: var(--space-4);
}

.topic-entry input {
  flex: 1;
  min-width: 0;
  border: 1px solid var(--paper-edge);
  border-radius: var(--radius-control);
  padding: 10px 12px;
  background: var(--paper);
  color: var(--paper-ink);
  font-size: 0.88rem;
}

.topic-entry input:focus {
  border-color: var(--accent);
}

.topic-feedback {
  margin-top: var(--space-3);
  padding: 10px 12px;
  border-left: 3px solid var(--accent);
  background: var(--paper-muted);
  color: #514d46;
  font-size: 0.82rem;
  line-height: 1.5;
}

.draft-section {
  margin-bottom: clamp(42px, 7vw, 72px);
  padding-top: 24px;
  border-top: 1px solid var(--paper-edge);
}

.draft-section:first-child {
  border-top: 3px double var(--paper-edge);
}

.draft-section-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
}

.draft-section h3 {
  margin: 0 0 var(--space-3);
  color: var(--paper-ink);
  font-family: var(--font-academic);
  font-size: clamp(1.8rem, 4vw, 2.35rem);
  font-weight: 500;
  line-height: 1.15;
}

.hint-details {
  margin-bottom: var(--space-3);
}

.hint-panel {
  margin-top: var(--space-2);
  padding: var(--space-3) var(--space-4);
  border-left: 2px solid var(--paper-edge);
  background: var(--paper-muted);
}

.framework-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: var(--space-2);
  padding-bottom: var(--space-2);
  border-bottom: 1px solid var(--paper-edge);
}

.framework-title {
  color: var(--accent-strong);
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
}

.framework-row,
.hint-row {
  color: var(--paper-muted-ink);
  font-size: 0.8rem;
  line-height: 1.5;
}

.hint-row {
  display: flex;
  gap: var(--space-2);
  margin-top: 6px;
}

.slot-tag {
  display: inline-flex;
  flex: 0 0 28px;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 22px;
  border: 1px solid #aaa399;
  color: #625d54;
  font-size: 0.62rem;
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;
}

.notion-editor {
  width: 100%;
  min-height: 220px;
  overflow: hidden;
  field-sizing: content;
  resize: none;
  border: 0;
  border-left: 2px solid transparent;
  padding: 10px 0 10px 16px;
  background: transparent;
  color: var(--paper-ink);
  font-family: var(--font-academic);
  font-size: clamp(1.02rem, 2vw, 1.12rem);
  line-height: 1.75;
  outline: none;
}

.notion-editor:focus {
  border-left-color: var(--accent);
  background: #fdfbf6;
}

.notion-editor:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
}

.notion-editor::placeholder {
  color: #aaa399;
}

.references-editor {
  overflow-y: auto;
  resize: vertical;
  white-space: normal;
}

.references-editor:empty::before {
  color: #aaa399;
  content: attr(data-placeholder);
}

.references-editor :deep(.reference-entry) {
  margin: 0 0 10px 24px;
  text-indent: -24px;
  line-height: 1.55;
}

.selection-toolbar {
  position: fixed;
  z-index: 50;
  display: flex;
  flex-direction: column;
  gap: 5px;
  width: 270px;
  padding: var(--space-2);
  border: 1px solid var(--paper-edge);
  border-radius: var(--radius-surface);
  background: var(--paper);
  box-shadow: 0 12px 28px rgba(38, 36, 31, 0.16);
}

.toolbar-label {
  color: var(--accent-strong);
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
}

.selection-toolbar button {
  border: 0;
  border-radius: var(--radius-control);
  padding: 8px 12px;
  background: transparent;
  color: #514d46;
  font-size: 0.8rem;
  text-align: left;
}

.selection-toolbar button:hover:not(:disabled) {
  background: var(--paper-muted);
}

.selection-toolbar .btn-primary {
  background: var(--accent);
  color: var(--paper);
  text-align: center;
}

.toolbar-divider {
  height: 1px;
  margin: 3px 0;
  background: var(--paper-edge);
}

.toolbar-chat {
  display: flex;
  align-items: flex-end;
  gap: 6px;
}

.toolbar-chat textarea {
  flex: 1;
  min-height: 54px;
  max-height: 92px;
  resize: vertical;
  border: 1px solid var(--paper-edge);
  border-radius: var(--radius-control);
  padding: var(--space-2);
  background: var(--paper);
  font-size: 0.75rem;
  line-height: 1.35;
}

.floating-chat-button {
  position: fixed;
  right: 34px;
  bottom: 28px;
  z-index: 40;
  width: 58px;
  height: 58px;
  border: 1px solid var(--paper-edge);
  border-radius: 50%;
  background: var(--paper);
  box-shadow: 0 8px 24px rgba(38, 36, 31, 0.14);
  cursor: grab;
  touch-action: none;
}

.floating-chat-button:active {
  cursor: grabbing;
}

.floating-chat-button img {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  object-fit: cover;
}

.floating-chat-hint {
  position: fixed;
  right: 104px;
  bottom: 42px;
  z-index: 41;
  width: 230px;
  padding: 10px 12px;
  border: 1px solid var(--paper-edge);
  border-radius: var(--radius-surface);
  background: var(--paper);
  box-shadow: 0 8px 24px rgba(38, 36, 31, 0.14);
  color: #514d46;
  font-size: 0.75rem;
  line-height: 1.4;
}

.floating-chat-hint::after {
  position: absolute;
  right: -7px;
  bottom: 18px;
  width: 12px;
  height: 12px;
  transform: rotate(-45deg);
  border-right: 1px solid var(--paper-edge);
  border-bottom: 1px solid var(--paper-edge);
  background: var(--paper);
  content: "";
}

.assistant-drawer {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 60;
  display: flex;
  flex-direction: column;
  height: 100%;
  max-width: calc(100vw - 160px);
  padding: var(--space-6);
  border-left: 1px solid var(--paper-edge);
  background: #f6f3ec;
}

.drawer-resize-handle {
  position: absolute;
  top: 0;
  left: -7px;
  width: 14px;
  height: 100%;
  cursor: ew-resize;
  touch-action: none;
}

.drawer-resize-handle::after {
  position: absolute;
  top: 0;
  left: 6px;
  width: 1px;
  height: 100%;
  background: var(--accent);
  opacity: 0;
  transition: opacity 150ms ease;
  content: "";
}

.drawer-resize-handle:hover::after {
  opacity: 1;
}

.drawer-resize-handle:focus-visible::after {
  opacity: 1;
}

.drawer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  padding-bottom: var(--space-3);
  border-bottom: 3px double var(--paper-edge);
}

.drawer-header h2 {
  margin: 0;
  color: var(--paper-ink);
  font-family: var(--font-academic);
  font-size: 1.25rem;
  font-weight: 600;
}

.drawer-tabs {
  display: flex;
  gap: 2px;
  margin: var(--space-4) 0;
  padding: 3px;
  border: 1px solid var(--paper-edge);
  border-radius: var(--radius-control);
  background: var(--paper-muted);
}

.drawer-tabs button {
  flex: 1;
  border: 0;
  border-radius: 4px;
  padding: 8px;
  background: transparent;
  color: var(--paper-muted-ink);
}

.drawer-tabs .drawer-tab-active {
  border: 1px solid var(--paper-edge);
  background: var(--paper);
  color: var(--accent-strong);
  font-weight: 700;
}

.drawer-chat,
.reference-library {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
}

.reference-library { overflow-y: auto; padding-right: 4px; }

.drawer-messages {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-2);
  min-height: 0;
  overflow-y: auto;
}

.drawer-message {
  display: flex;
}

.drawer-user {
  justify-content: flex-end;
}

.drawer-bubble {
  max-width: 86%;
  padding: 10px 12px;
  border: 1px solid var(--paper-edge);
  border-radius: var(--radius-surface);
  background: var(--paper-muted);
  color: #3d3933;
  font-size: 0.86rem;
  line-height: 1.55;
  white-space: pre-wrap;
}

.drawer-user .drawer-bubble {
  border-color: var(--accent);
  background: var(--accent);
  color: var(--paper);
}

.drawer-user .tool-user-bubble {
  max-width: 92%;
  border-color: var(--paper-edge);
  background: var(--paper);
  color: #3d3933;
}

.tool-message-label {
  margin-bottom: 7px;
  color: var(--accent-strong);
  font-size: 0.65rem;
  font-weight: 800;
  text-transform: uppercase;
}

.tool-user-bubble blockquote {
  margin: 0;
  padding: 8px 10px;
  border-left: 3px solid var(--accent);
  background: var(--paper-muted);
  color: #514d46;
  white-space: pre-wrap;
}

.tool-message-note {
  margin-top: var(--space-2);
  color: var(--paper-muted-ink);
  font-size: 0.75rem;
}

.assistant-output {
  white-space: normal;
}

.assistant-output :deep(strong) {
  color: var(--paper-ink);
  font-weight: 700;
}

.assistant-output :deep(.assistant-bullet) {
  display: block;
  margin-top: 4px;
  padding-left: 14px;
  text-indent: -10px;
}

.drawer-composer {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: var(--space-2);
  margin-top: var(--space-3);
  padding-top: var(--space-3);
  border-top: 1px solid var(--paper-edge);
}

.composer-tools {
  grid-column: 1 / -1;
  display: flex;
  gap: var(--space-2);
}

.composer-tools button {
  border: 1px solid var(--paper-edge);
  border-radius: var(--radius-control);
  padding: 7px 10px;
  background: var(--paper);
  color: #514d46;
  font-size: 0.75rem;
}

.composer-tools button:hover:not(:disabled) {
  background: var(--paper-muted);
}

.composer-tools button:disabled {
  opacity: 0.5;
}

.drawer-composer textarea {
  min-height: 70px;
  resize: none;
  border: 1px solid var(--paper-edge);
  border-radius: var(--radius-control);
  padding: 10px;
  background: var(--paper);
  color: var(--paper-ink);
  font-size: 0.86rem;
}

.library-card {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  margin-bottom: 10px;
  padding: var(--space-3);
  border: 1px solid var(--paper-edge);
  background: var(--paper);
}

.library-card-top {
  display: flex;
  justify-content: space-between;
  gap: 10px;
}

.reference-card-tools {
  display: flex;
  flex-shrink: 0;
  gap: 5px;
}

.reference-edit-btn {
  color: var(--paper-muted-ink);
}

.reference-apa {
  padding: 8px 10px;
  border-left: 2px solid var(--paper-edge);
  background: var(--paper-muted);
  color: #3d3933;
  font-family: var(--font-academic);
  font-size: 0.77rem;
  line-height: 1.5;
}

.reference-open-link {
  align-self: flex-start;
  padding: 5px 8px;
  border: 1px solid #c8a391;
  border-radius: var(--radius-control);
  background: var(--accent-soft);
  color: var(--accent-strong);
  font-size: 0.75rem;
  font-weight: 700;
  text-decoration: none;
}

.reference-open-link:hover {
  background: #ecdcd2;
}

.reference-note {
  display: grid;
  grid-template-columns: 76px 1fr;
  gap: var(--space-2);
  color: var(--paper-muted-ink);
  font-size: 0.75rem;
  line-height: 1.45;
}

.reference-note b {
  color: var(--accent-strong);
  font-size: 0.65rem;
  text-transform: uppercase;
}

.reference-empty {
  padding: var(--space-3);
  border: 1px solid var(--paper-edge);
  background: var(--paper-muted);
  color: var(--paper-muted-ink);
  font-size: 0.8rem;
  font-style: italic;
  text-align: center;
}

.add-reference-full {
  width: 100%;
  min-height: 38px;
  margin-top: 2px;
  border: 1px dashed #c8a391;
  border-radius: var(--radius-control);
  background: var(--accent-soft);
  color: var(--accent);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  font-size: 0.8rem;
  font-weight: 700;
}

.add-reference-icon {
  font-size: 1.25rem;
  line-height: 1;
}

.icon-btn {
  width: 30px;
  height: 30px;
  border: 1px solid var(--paper-edge);
  border-radius: var(--radius-control);
  background: var(--paper);
  color: var(--accent-strong);
}

.btn-icon {
  border: 0;
  border-radius: var(--radius-control);
  padding: 5px 9px;
  background: transparent;
  color: var(--paper-muted-ink);
  font-size: 1.1rem;
}

.btn-icon:hover {
  background: var(--paper-muted);
}

.btn-primary,
.btn-secondary {
  border-radius: var(--radius-control);
  padding: 9px 14px;
  font-size: 0.86rem;
}

.compact-btn {
  min-height: 32px;
  padding: 6px 10px;
  font-size: 0.75rem;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(38, 36, 31, 0.48);
}

.modal {
  display: flex;
  flex-direction: column;
  width: 560px;
  max-width: 95vw;
  max-height: 85vh;
  border: 1px solid var(--paper-edge);
  border-radius: var(--radius-surface);
  background: var(--paper);
  box-shadow: 0 18px 48px rgba(38, 36, 31, 0.2);
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px 0;
}

.modal-header h2 {
  margin: 0;
  color: var(--paper-ink);
  font-family: var(--font-academic);
  font-size: 1.1rem;
  font-weight: 700;
}

.modal-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 16px 24px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  padding: 12px 24px 20px;
  border-top: 1px solid var(--paper-edge);
}

.reference-form {
  display: flex;
  flex-direction: column;
  gap: 7px;
}

.reference-form > * {
  flex-shrink: 0;
}

.field-label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-top: 6px;
}

.field-label-row .field-label {
  margin-top: 0;
}

.field-label {
  margin-top: 6px;
  color: var(--paper-muted-ink);
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
}

.ref-input {
  width: 100%;
  flex-shrink: 0;
  resize: vertical;
  border: 1px solid var(--paper-edge);
  border-radius: var(--radius-control);
  padding: 8px 10px;
  background: #fffefa;
  color: var(--paper-ink);
  font-size: 0.78rem;
  line-height: 1.45;
}

textarea.ref-input {
  min-height: 78px;
}

.mini-action-btn {
  padding: 5px 8px;
  font-size: 0.7rem;
  white-space: nowrap;
}

.paraphrase-options-btn {
  align-self: flex-start;
  margin-top: 4px;
}

.paraphrase-options {
  display: flex;
  flex-direction: column;
  gap: 7px;
}

.paraphrase-options button {
  border: 1px solid var(--paper-edge);
  border-radius: var(--radius-control);
  padding: 8px 10px;
  background: var(--paper);
  color: #3d3933;
  font-size: 0.78rem;
  line-height: 1.45;
  text-align: left;
}

.paraphrase-options button:hover {
  border-color: #c8a391;
  background: var(--accent-soft);
}

.form-error {
  margin-top: 6px;
  padding: 8px 10px;
  border: 1px solid #d4a792;
  border-radius: var(--radius-control);
  background: #f6eee8;
  color: #8e402e;
  font-size: 0.75rem;
  line-height: 1.45;
}

.reference-modal-footer {
  gap: var(--space-2);
}

@media (max-width: 1080px) {
  .workspace-body {
    grid-template-columns: minmax(0, 820px);
  }

  .page-outline {
    display: none;
  }
}

@media (max-width: 900px) {
  .workspace-topbar {
    grid-template-columns: minmax(0, 1fr) auto;
    gap: var(--space-3);
  }

  .draft-progress {
    grid-column: 1 / 2;
    grid-row: 2;
  }

  .workspace-actions {
    flex-wrap: wrap;
  }
}

@media (max-width: 767px) {
  .notion-workspace button,
  .notion-workspace input {
    min-height: 44px;
  }

  .workspace-topbar {
    grid-template-columns: minmax(0, 1fr);
    gap: var(--space-2);
    min-height: 0;
    padding: 8px 12px;
  }

  .workspace-topbar__identity,
  .draft-progress {
    grid-column: 1;
    grid-row: auto;
  }

  .workspace-document-title span {
    display: none;
  }

  .workspace-actions {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    width: 100%;
  }

  .workspace-actions .save-status,
  .workspace-actions .submitted-lock-note,
  .workspace-actions .ui-button:nth-of-type(3) {
    grid-column: 1 / -1;
  }

  .workspace-actions .ui-button {
    padding-inline: 8px;
    font-size: 0.75rem;
  }

  .workspace-body {
    padding: 18px 12px 72px;
  }

  .drawer-open-workspace .workspace-body {
    padding-right: 8px;
    padding-left: 8px;
  }

  .writing-paper {
    min-height: calc(100vh - 220px);
    padding: 26px 18px;
  }

  .drawer-open-workspace .writing-paper {
    padding-right: 12px;
    padding-left: 12px;
  }

  .paper-masthead {
    margin-bottom: var(--space-8);
  }

  .topic-title-row {
    align-items: stretch;
    flex-direction: column;
  }

  .topic-edit-btn {
    align-self: flex-start;
  }

  .topic-grid {
    grid-template-columns: 1fr;
  }

  .topic-entry {
    flex-direction: column;
  }

  .cefr-control button,
  .icon-btn,
  .btn-icon,
  .compact-btn {
    min-width: 44px;
    min-height: 44px;
    height: auto;
  }

  .reference-open-link,
  .topic-options summary,
  .hint-details summary {
    display: inline-flex;
    align-items: center;
    min-height: 44px;
  }

  .submitted-score-card {
    grid-template-columns: 1fr;
  }

  .draft-section-heading {
    align-items: flex-start;
    flex-direction: column;
  }

  .notion-editor {
    padding-left: 10px;
  }

  .assistant-drawer {
    padding: var(--space-4);
  }

  .drawer-resize-handle {
    left: -22px;
    width: 44px;
  }

  .drawer-resize-handle::after {
    left: 21px;
  }

  .drawer-composer {
    grid-template-columns: 1fr;
  }

  .drawer-composer .btn-primary {
    width: 100%;
  }

  .composer-tools {
    flex-direction: column;
  }

  .modal-header,
  .modal-body,
  .modal-footer {
    padding-right: var(--space-4);
    padding-left: var(--space-4);
  }
}

@media (prefers-reduced-motion: reduce) {
  .notion-workspace,
  .draft-progress__track span,
  .drawer-resize-handle::after {
    transition: none;
  }
}
</style>
